import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
type MaxWidthToken = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
const MAX_WIDTH_CLASS: Record<MaxWidthToken, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl'
};
interface CenteredModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Tailwind max-width token for the inner panel. Default 'md'. */
  maxWidth?: MaxWidthToken;
  /** Extra classes on the inner panel (e.g. background, padding, height). */
  panelClassName?: string;
  /** Tailwind classes on the backdrop. Default: bg-slate-900/40 backdrop-blur-sm. */
  backdropClassName?: string;
  /** Disable closing on backdrop click (e.g. for commit-flows). */
  disableBackdropClose?: boolean;
  /**
   * Toegankelijke naam van de dialoog. Verplicht: een dialoog zonder naam
   * wordt door een schermlezer aangekondigd als "dialoog", zonder te zeggen
   * waar hij over gaat — en juist die aankondiging is het enige signaal dat er
   * iets bovenop de pagina is verschenen.
   */
  ariaLabel: string;
  /** Optional spring tuning for panel enter/exit. */
  springBounce?: number;
  springDuration?: number;
  children: React.ReactNode;
}
/**
 * CenteredModal — Shared modal shell for the Learning Lab.
 *
 * Owns:
 * - AnimatePresence + fixed-inset positioning
 * - Backdrop fade + click-to-close
 * - Panel scale/y enter-exit
 * - Max-width sizing
 * - Dialoogsemantiek: role/aria-modal, focusvang en Escape
 *
 * Consumers provide the panel content (header, body, actions).
 *
 * De dialoogsemantiek zat hier NIET in, terwijl elk leerlab-venster
 * (ChallengeAcceptModal, ModeIntroSheet, FluencyModulePicker) hierop leunt.
 * Een kind dat met het toetsenbord werkt tabde er dwars doorheen naar de
 * pagina erachter — de kaarten van het leerlab bleven bereikbaar terwijl er
 * een venster voor stond. Omdat het één gedeelde shell is, geldt de reparatie
 * meteen voor alle drie.
 *
 * Part of the Learning Lab v1.4 Wave C structural refactor (F-UI-7).
 */
export function CenteredModal({
  isOpen,
  onClose,
  maxWidth = 'md',
  panelClassName = 'bg-white rounded-3xl shadow-2xl overflow-hidden',
  backdropClassName = 'absolute inset-0 bg-slate-900/40 backdrop-blur-sm',
  disableBackdropClose = false,
  ariaLabel,
  springBounce,
  springDuration,
  children
}: CenteredModalProps) {
  const widthClass = MAX_WIDTH_CLASS[maxWidth];
  const trapRef = useFocusTrap(isOpen);

  // Escape sluit de dialoog. Bewust óók wanneer sluiten via de achtergrond is
  // uitgezet: `disableBackdropClose` bestaat om een onbedoelde klik naast het
  // venster op te vangen, niet om het kind erin op te sluiten. Zonder Escape
  // was een venster met het toetsenbord alleen te verlaten door de sluitknop
  // te vinden.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-3164" data-mp="7v74qp2j5thd">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-3165" data-mp="7xjisr563jp5">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className={backdropClassName} onClick={disableBackdropClose ? undefined : onClose} aria-hidden="true" data-id="element-3166" data-mp="2yozlo3aft4i" />
        
          <motion.div ref={trapRef} role="dialog" aria-modal="true" aria-label={ariaLabel} initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={springBounce !== undefined || springDuration !== undefined ? {
        type: 'spring',
        bounce: springBounce ?? 0.3,
        duration: springDuration ?? 0.45
      } : undefined} className={`relative w-full ${widthClass} ${panelClassName}`} data-id="element-3167" data-mp="3lav8mxhfhceq">
          
            {children}
          </motion.div>
        </div>}
    </AnimatePresence>;
}